import * as generatedApi from './generated';

export const listId = 'LIST' as const;

const getListId = <TagType extends string = never>(type: TagType): { type: TagType; id: string | number } => {
  return { type, id: listId };
};

function provideListTags<TagType extends string = never, ResultType = object>(
  result: ResultType | undefined,
  type: TagType,
  idMap: (result: ResultType) => (string | number)[]
) {
  if (result) {
    return [...idMap(result).map((id) => ({ type, id })), getListId(type)];
  }
  return [getListId(type)];
}

function provideTag<TagType extends string = never>(type: TagType, id: string | number) {
  return [{ type, id }];
}

export const enhancedApi = generatedApi.api.enhanceEndpoints({
  addTagTypes: ['Account/LoginInvitation', 'Account/DataObject/Counts', 'Account/ContactMessages/Counts'],
  endpoints: {
    // public endpoints
    getV1Attribute: {
      providesTags: (result) => provideListTags(result, 'Attribute', (res) => res.attributes.map((a) => a.id)),
    },
    getV1Category: {
      providesTags: (result) => provideListTags(result, 'Category', (res) => res.categories.map((c) => c.id)),
    },
    getV1DataObject: {
      providesTags: (result) => provideListTags(result, 'DataObject', (res) => res.items.map((d) => d.id)),
    },
    getV1DataObjectById: {
      providesTags: (result) => provideTag('DataObject', result?.id || 0),
    },
    getV1Favorite: {
      providesTags: (result) => provideListTags(result, 'Favorite', (res) => res.dataObjectIds),
    },
    deleteV1Favorite: {
      invalidatesTags: [getListId('Favorite')],
    },
    postV1Favorite: {
      invalidatesTags: [getListId('Favorite')],
    },
    getV1SearchAlerts: {
      providesTags: (result) => provideListTags(result, 'SearchAlerts', (x) => x.items.map((i) => i.id)),
    },
    getV1SearchAlertsById: {
      providesTags: (result) => provideTag('SearchAlerts', result?.id || 0),
    },
    putV1SearchAlertsById: {
      invalidatesTags: (_, __, req) => [getListId('SearchAlerts'), { type: 'SearchAlerts', id: req.id }],
    },
    putV1SearchAlertsByIdActivate: {
      invalidatesTags: (_, __, req) => [getListId('SearchAlerts'), { type: 'SearchAlerts', id: req.id }],
    },
    postV1SearchAlerts: {
      invalidatesTags: [getListId('SearchAlerts')],
    },
    deleteV1SearchAlertsById: {
      invalidatesTags: [getListId('SearchAlerts')],
    },

    // account endpoints
    getV1AccountAccount: {
      providesTags: (result) => provideListTags(result, 'Account/Account', (res) => res.accounts.map((a) => a.id)),
    },
    getV1AccountAccountById: {
      providesTags: (result) => provideTag('Account/Account', result?.account.accountId || 0),
    },
    postV1AccountAccount: {
      invalidatesTags: [getListId('Account/Account')],
    },
    putV1AccountAccountById: {
      invalidatesTags: (result) => [
        getListId('Account/Account'),
        { type: 'Account/Account', id: result?.account.accountId || 0 },
      ],
    },
    getV1AccountDataObject: {
      providesTags: (result) => provideListTags(result, 'Account/DataObject', (res) => res.items.map((d) => d.id)),
    },
    getV1AccountDataObjectCounts: {
      providesTags: ['Account/DataObject/Counts'],
    },
    getV1AccountContactMessages: {
      providesTags: (result) => provideListTags(result, 'Account/ContactMessages', (res) => res.items.map((c) => c.id)),
    },
    getV1AccountContactMessagesById: {
      providesTags: (result) => provideTag('Account/ContactMessages', result?.id || 0),
      // the read state shown in a thread changes from outside it, so nothing is worth keeping
      keepUnusedDataFor: 0,
    },
    getV1AccountContactMessagesCounts: {
      providesTags: ['Account/ContactMessages/Counts'],
    },
    // Both read-state mutations invalidate only when they actually changed something: selecting a
    // conversation always marks it read, and refetching the lists and the counts for a conversation
    // that was read already would be pure noise. The advert list is in there because its rows carry
    // the unread count of the advert.
    putV1AccountContactMessagesRead: {
      invalidatesTags: (result) =>
        result?.markedCount
          ? [getListId('Account/ContactMessages'), 'Account/ContactMessages/Counts', getListId('Account/DataObject')]
          : [],
    },
    putV1AccountContactMessagesByIdUnread: {
      invalidatesTags: (result) =>
        result?.markedCount
          ? [getListId('Account/ContactMessages'), 'Account/ContactMessages/Counts', getListId('Account/DataObject')]
          : [],
    },
    postV1AccountContactMessagesByIdReply: {
      invalidatesTags: (_, __, req) => [
        getListId('Account/ContactMessages'),
        { type: 'Account/ContactMessages', id: req.id },
      ],
    },
    getV1AccountDataObjectById: {
      providesTags: (result) => provideTag('Account/DataObject', result?.dataObject.id || 0),
      // always refetch details
      keepUnusedDataFor: 0,
    },
    getV1AccountPrintAdvertById: {
      providesTags: (result) => provideTag('Account/DataObject', result?.dataObject.id || 0),
      // always refetch details
      keepUnusedDataFor: 0,
    },
    postV1AccountDataObject: {
      invalidatesTags: [getListId('Account/DataObject'), 'Account/DataObject/Counts'],
    },
    putV1AccountDataObjectById: {
      invalidatesTags: (result) => [
        getListId('Account/DataObject'),
        { type: 'Account/DataObject', id: result?.dataObject.id || 0 },
        'Account/DataObject/Counts',
      ],
    },
    getV1AccountPublicationOptions: {
      keepUnusedDataFor: 0,
    },
    postV1AccountPublicationDataObjectById: {
      invalidatesTags: [
        getListId('Account/Contracts'),
        getListId('Account/DataObject'),
        'Account/Publication',
        'Account/DataObject/Counts',
      ],
    },
    postV1AccountPrintAdvertPublish: {
      invalidatesTags: [getListId('Account/DataObject'), 'Account/PrintAdvert', 'Account/DataObject/Counts'],
    },
    putV1AccountDataObjectByIdActivate: {
      invalidatesTags: (_, __, req) => [
        getListId('Account/DataObject'),
        { type: 'Account/DataObject', id: req.id },
        'Account/DataObject/Counts',
      ],
    },
    putV1AccountDataObjectByIdDeactivate: {
      invalidatesTags: (_, __, req) => [
        getListId('Account/DataObject'),
        { type: 'Account/DataObject', id: req.id },
        'Account/DataObject/Counts',
      ],
    },
    deleteV1AccountDataObjectById: {
      invalidatesTags: [getListId('Account/DataObject'), 'Account/DataObject/Counts'],
    },
    postV1AccountDataObjectByIdDuplicate: {
      invalidatesTags: [getListId('Account/DataObject'), 'Account/DataObject/Counts'],
    },
    getV1AccountAddress: {
      providesTags: (result) => provideListTags(result, 'Account/Address', (x) => x.addresses.map((a) => a.id)),
    },
    putV1AccountAddressById: {
      invalidatesTags: (_, __, req) => [
        getListId('Account/Address'),
        { type: 'Account/Address', id: req.id },
        'Account/Account',
      ],
    },
    postV1AccountAddress: {
      invalidatesTags: [getListId('Account/Address'), 'Account/Account'],
    },
    deleteV1AccountAddressById: {
      invalidatesTags: [getListId('Account/Address')],
    },
    getV1AccountContactPerson: {
      providesTags: (result) =>
        provideListTags(result, 'Account/ContactPerson', (x) => x.contactPersons.map((c) => c.id)),
    },
    putV1AccountContactPersonById: {
      invalidatesTags: (_, __, req) => [
        getListId('Account/ContactPerson'),
        { type: 'Account/ContactPerson', id: req.id },
        'Account/ContactPerson',
      ],
    },
    postV1AccountContactPerson: {
      invalidatesTags: [getListId('Account/ContactPerson'), 'Account/ContactPerson'],
    },
    deleteV1AccountContactPersonById: {
      invalidatesTags: [getListId('Account/ContactPerson')],
    },
    getV1AccountLogins: {
      providesTags: (result) => provideListTags(result, 'Account/Logins', (x) => x.items.map((l) => l.id)),
    },
    postV1AccountLogins: {
      invalidatesTags: [getListId('Account/Logins')],
    },
    putV1AccountLoginsSetRole: {
      invalidatesTags: (_, __, req) => [
        getListId('Account/Logins'),
        { type: 'Account/Logins', id: req.accountLoginsSetLoginRoleRequest.id },
      ],
    },
    deleteV1AccountLoginsById: {
      invalidatesTags: [getListId('Account/Logins')],
    },
    getV1AccountLoginsInvitations: {
      providesTags: (result) =>
        provideListTags(result, 'Account/LoginInvitation', (x) => x.items.map((i) => i.accountId)),
    },
    putV1AccountLoginsAcceptInvitation: {
      invalidatesTags: [getListId('Account/LoginInvitation')],
    },
    putV1AccountLoginsRejectInvitation: {
      invalidatesTags: [getListId('Account/LoginInvitation')],
    },
    putV1AccountDataObjectByIdTopListing: {
      invalidatesTags: (_, __, req) => [getListId('Account/DataObject'), { type: 'Account/DataObject', id: req.id }],
    },
    getV1AccountContracts: {
      providesTags: (result) => provideListTags(result, 'Account/Contracts', (x) => x.items.map((i) => i.id)),
    },
    postV1AccountContractsCreate: {
      invalidatesTags: [getListId('Account/Contracts')],
    },
    postV1AccountContractsUpgrade: {
      invalidatesTags: (_, __, req) => [
        getListId('Account/Contracts'),
        { type: 'Account/Contracts', id: req.accountContractsUpgradeContractRequest.contractId },
      ],
    },
    getV1AccountPrintAdvertQuote: {
      keepUnusedDataFor: 0,
    },
    getV1AccountPaymentsContract: {
      keepUnusedDataFor: 0,
    },
    getV1AccountPaymentsStatus: {
      keepUnusedDataFor: 0,
    },
    getV1PrintTitleNextIssueInfo: {
      providesTags: ['PrintTitle'],
    },
  },
});
